import {
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AppError } from '../../api/errors.js';
import {
  DataHubClientProvider,
  useDataHubClient,
} from '../../context/DataHubClientContext.js';
import type { AccessControlGroup } from '../../services/accessControlService.interface.js';
import { AccessControlService } from '../../services/accessControlService.js';

import type { PrivateWorkspaceRoles } from './privateWorkspace.interface.js';
import {
  mapDatasetRolesToWorkspaceRoles,
  updatePrivateWorkspaceRoles,
} from './privateWorkspace.js';
import { WorkspaceRoleEditor } from './WorkspaceRoleEditor.js';

export interface PrivateWorkspaceAccessSectionProps {
  anchorDatasetId: string;
  readOnly?: boolean;
}

async function resolveGroups(
  ids: string[],
  service: AccessControlService,
): Promise<AccessControlGroup[]> {
  return Promise.all(ids.map((id) => service.getAccessControlGroup(id)));
}

function PrivateWorkspaceAccessFields({
  anchorDatasetId,
  readOnly = false,
}: PrivateWorkspaceAccessSectionProps) {
  const client = useDataHubClient();
  const service = useMemo(() => new AccessControlService(client), [client]);
  const [roles, setRoles] = useState<{
    owner: AccessControlGroup[];
    contributor: AccessControlGroup[];
    reader: AccessControlGroup[];
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [originalRoles, setOriginalRoles] =
    useState<PrivateWorkspaceRoles | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const loadRoles = useCallback(() => {
    let cancelled = false;
    setLoadError(null);
    setRoles(null);
    void service
      .getDatasetGroupRole(anchorDatasetId)
      .then(async ({ roles: datasetRoles }) => {
        const workspaceRoles = mapDatasetRolesToWorkspaceRoles(datasetRoles);
        const [owner, contributor, reader] = await Promise.all([
          resolveGroups(datasetRoles.owner, service),
          resolveGroups(datasetRoles.contributor, service),
          resolveGroups(datasetRoles.consumer, service),
        ]);
        if (!cancelled) {
          setOriginalRoles(workspaceRoles);
          setRoles({ owner, contributor, reader });
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          if (error instanceof AppError && error.statusCode === 404) {
            setOriginalRoles({ owner: [], contributor: [], reader: [] });
            setRoles({ owner: [], contributor: [], reader: [] });
            return;
          }
          setLoadError(
            error instanceof Error
              ? error.message
              : 'Failed to load workspace access.',
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [anchorDatasetId, service]);

  useEffect(() => loadRoles(), [loadRoles]);

  const saveRoles = async () => {
    if (roles === null || originalRoles === null) return;
    setSaving(true);
    setSaveError(null);
    const roleIds: PrivateWorkspaceRoles = {
      owner: roles.owner.map((group) => group.id),
      contributor: roles.contributor.map((group) => group.id),
      reader: roles.reader.map((group) => group.id),
    };
    try {
      await updatePrivateWorkspaceRoles(
        anchorDatasetId,
        originalRoles,
        roleIds,
        service,
      );
      setOriginalRoles(roleIds);
    } catch (error) {
      setSaveError(
        error instanceof Error
          ? error.message
          : 'Failed to update workspace access.',
      );
    } finally {
      setSaving(false);
    }
  };

  if (loadError !== null) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{loadError}</MessageBarBody>
        <Button onClick={() => loadRoles()}>Retry</Button>
      </MessageBar>
    );
  }

  if (roles === null || originalRoles === null) {
    return <Spinner label="Loading workspace access..." />;
  }

  return (
    <>
      <Text weight="semibold" size={400}>
        Access
      </Text>
      <Text>
        Access is inherited from the workspace&apos;s restricted Data Lake
        dataset. Saving preserves groups assigned to roles outside this editor.
      </Text>
      <WorkspaceRoleEditor
        label="Owners"
        description="Can manage workspace settings and access."
        groups={roles.owner}
        onChange={(owner) => setRoles({ ...roles, owner })}
        disabled={saving || readOnly}
      />
      <WorkspaceRoleEditor
        label="Contributors"
        description="Can create and update workspace resources."
        groups={roles.contributor}
        onChange={(contributor) => setRoles({ ...roles, contributor })}
        disabled={saving || readOnly}
      />
      <WorkspaceRoleEditor
        label="Readers"
        description="Can view workspace resources."
        groups={roles.reader}
        onChange={(reader) => setRoles({ ...roles, reader })}
        disabled={saving || readOnly}
      />
      {saveError !== null && (
        <MessageBar intent="error">
          <MessageBarBody>{saveError}</MessageBarBody>
        </MessageBar>
      )}
      {roles.owner.length === 0 &&
        (roles.contributor.length > 0 || roles.reader.length > 0) && (
          <MessageBar intent="warning">
            <MessageBarBody>
              Select an Owner group before assigning Contributors or Readers.
            </MessageBarBody>
          </MessageBar>
        )}
      {!readOnly && (
        <Button
          appearance="primary"
          disabled={saving || roles.owner.length === 0}
          onClick={() => void saveRoles()}
        >
          {saving ? 'Saving access...' : 'Save access'}
        </Button>
      )}
    </>
  );
}

export function PrivateWorkspaceAccessSection(
  props: PrivateWorkspaceAccessSectionProps,
) {
  return (
    <DataHubClientProvider>
      <PrivateWorkspaceAccessFields {...props} />
    </DataHubClientProvider>
  );
}
